import React from 'react';
import { Autocomplete, Box, Button, TextField, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useMediaFiles } from '../../hooks/mediaFiles';
import mediaFilesService from '../../api/mediaFilesService';
import type { MediaFile } from '../../types/types';
import { t } from '../../i18n/t';
import MediaFilePreview, { mediaKindFromMime } from './MediaFilePreview';

export type MediaFilePickerKind = 'image' | 'av' | 'all';

interface MediaFilePickerProps {
    value: number | null;
    onChange: (id: number | null) => void;
    labelKey?: string;
    required?: boolean;
    kind?: MediaFilePickerKind;
}

function matchesKind(file: MediaFile, kind: MediaFilePickerKind): boolean {
    const mediaKind = mediaKindFromMime(file.mimeType);
    if (kind === 'all') return true;
    if (kind === 'image') return mediaKind === 'image';
    return mediaKind === 'audio' || mediaKind === 'video';
}

const MediaFilePicker: React.FC<MediaFilePickerProps> = ({
    value,
    onChange,
    labelKey = 'postureInstructions.imageSection',
    required = false,
    kind = 'image',
}) => {
    const { data: files = [], isLoading } = useMediaFiles();
    const options = files.filter((f) => matchesKind(f, kind));
    const selected = options.find((f) => f.id === value) ?? files.find((f) => f.id === value) ?? null;

    const selectLabel =
        kind === 'av' ? t('mediaFiles.selectAv') : t('postureInstructions.selectImage');
    const selectPlaceholder =
        kind === 'av'
            ? t('mediaFiles.selectAvPlaceholder')
            : t('postureInstructions.selectImagePlaceholder');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle2">
                {t(labelKey)}
                {required ? ' *' : ''}
            </Typography>
            <Autocomplete
                options={options}
                value={selected}
                loading={isLoading}
                getOptionLabel={(opt) => opt.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, opt) => onChange(opt?.id ?? null)}
                renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MediaFilePreview
                            src={mediaFilesService.getFileUrl(option.id)}
                            mimeType={option.mimeType}
                            alt={option.name}
                            compact
                            sx={{ border: 1, borderColor: 'divider' }}
                        />
                        <Box>
                            <Typography variant="body2">{option.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                #{option.id} · {option.mimeType}
                            </Typography>
                        </Box>
                    </Box>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        label={selectLabel}
                        placeholder={selectPlaceholder}
                    />
                )}
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button size="small" onClick={() => onChange(null)} disabled={value == null}>
                    {t('postureInstructions.clearImage')}
                </Button>
                <Button size="small" component={Link} to="/dashboard/media-files">
                    {t('postureInstructions.openLibrary')}
                </Button>
            </Box>
            {value != null && (
                <MediaFilePreview
                    src={`${mediaFilesService.getFileUrl(value)}?t=${Date.now()}`}
                    mimeType={selected?.mimeType}
                    alt={selected?.name ?? ''}
                    sx={{ border: 1, borderColor: 'divider', alignSelf: 'flex-start' }}
                />
            )}
        </Box>
    );
};

export default MediaFilePicker;
